import React, { Fragment, memo, useCallback, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Loader, Text, Drawer, Modal, Icon } from '@Sage/hoi-poi-ui';

import { getFormTemplates, getForms, deleteForm, getForm } from '../../lib/services/forms';
import { useWeb5Context } from 'lib/Web5Context';

import FormsList from './FormsList';
import FormModal from './FormModal';
import FormDetails from './FormDetails';
import Signature from './Signature';
import FormsIcon from './icons/FormsIcon';
import { errorToast } from 'utils/toast';

import styles from './styles';
const useStyles = createUseStyles(styles, 'Forms');

const reducer = (state, action) => {
    switch (action.type) {
        case 'setActiveFormId':
            state = {
                ...state,
                activeForm: {
                    ...state.activeForm,
                    id: action.value,
                },
            };
            return state;
        case 'add':
            state = {
                ...state,
                activeForm: { id: null },
                formModalIsOpen: true,
            };
            return state;
        case 'setListIsLoading':
            state = {
                ...state,
                listIsLoading: action.value,
            };
            return state;
        case 'setForms':
            state = {
                ...state,
                forms: action.forms,
                hasNextPage: action.hasNextPage,
                listIsLoading: false,
            };
            return state;
        case 'setMoreForms':
            state = {
                ...state,
                forms: [...state.forms, ...action.forms],
                hasNextPage: action.hasNextPage,
                listIsLoading: false,
            };
            return state;
        case 'setError':
            state = {
                ...state,
                error: true,
                listIsLoading: false,
            };
            return state;
        case 'editForm':
            state = {
                ...state,
                activeForm: action.data,
                formModalIsOpen: true,
            };
            return state;
        case 'closeForm':
            state = {
                ...state,
                formModalIsOpen: false,
                reload: false,
                showImages: false,
                expandImages: true,
            };
            return state;
        case 'openDetail':
            state = {
                ...state,
                formModalIsOpen: false,
                detailsDrawerIsOpen: true,
                activeForm: action.data,
            };
            return state;
        case 'closeDetail':
            state = {
                ...state,
                detailsDrawerIsOpen: false,
            };
            return state;

        case 'setFormTemplates':
            state = {
                ...state,
                formTemplates: action.data,
            };
            return state;
        case 'setReload':
            state = {
                ...state,
                reload: action.value,
            };
            return state;
        case 'setFormTitle':
            state = {
                ...state,
                formTitle: action.value,
            };
            return state;
        case 'setSignatureIsOpen':
            state = {
                ...state,
                signatureIsOpen: action.value,
            };
            return state;
        case 'setShowImages':
            state = {
                ...state,
                showImages: action.value,
            };
            return state;
        case 'setExpandImages':
            state = {
                ...state,
                expandImages: action.value,
            };
            return state;
        case 'setIsLoading':
            state = {
                ...state,
                isLoading: action.value,
            };
            return state;
        case 'setConfirmModal':
            state = {
                ...state,
                confirmModal: action.data,
            };
            return state;

        case 'setDetailsDrawerIsOpen':
            state = {
                ...state,
                detailsDrawerIsOpen: action.value,
            };
            return state;
        case 'setActionsReady':
            state = {
                ...state,
                actionsReady: true,
            };
            return state;
        default:
            return state;
    }
};

const initialState = {
    formTemplates: [],
    forms: [],
    hasNextPage: true,
    isLoading: true,
    listIsLoading: false,
    hasError: false,
    detailsDrawerIsOpen: false,
    confirmModal: null,
    formModalIsOpen: false,
    signatureIsOpen: false,
    formTitle: '',
    activeForm: null,
    showImages: false,
    expandImages: true,
    reload: false,
    actionsReady: false,
};

const Forms = ({ entityId, add = false, setAdd, height, setSize, permissions }) => {
    const theme = useTheme();
    const classes = useStyles();
    const { t, bridgeBackend } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    const page = useRef(0);
    const gettingForms = useRef(false);

    const setFormId = useCallback((id) => dispatch({ type: 'setActiveFormId', value: id }), []);

    const showToast = useCallback(() => {
        errorToast({
            t,
            text: t('error_generalerror'),
            title: t('cfm_label_success'),
        });
    }, [t]);

    useEffect(() => {
        if (!add || state.formModalIsOpen) return;
        setAdd(false);
        if (!state.actionsReady) {
            return showToast();
        }
        dispatch({ type: 'add' });
    }, [add, setAdd, showToast, state.actionsReady, state.formModalIsOpen]);

    const getFormsList = useCallback(() => {
        if (gettingForms.current) return;
        gettingForms.current = true;
        dispatch({ type: 'setListIsLoading', value: true });
        getForms(entityId, page.current)
            .then((forms) => {
                if (!Array.isArray(forms)) {
                    gettingForms.current = false;
                    return dispatch({ type: 'setError' });
                }
                if (state.forms.length === 0 && forms.length !== 0) {
                    setSize && setSize({ height });
                }
                const type = page.current === 0 ? 'setForms' : 'setMoreForms';
                let hasNextPage = false;
                if (forms.length !== 0) {
                    page.current = page.current + 1;
                    hasNextPage = true;
                }
                dispatch({ type, forms, hasNextPage });
                gettingForms.current = false;
            })
            .catch(() => {
                dispatch({ type: 'setError' });
                gettingForms.current = false;
            });
    }, [entityId, height, setSize, state.forms.length]);

    const handleConfirmModalCancel = useCallback(
        () => dispatch({ type: 'setConfirmModal', data: null }),
        [],
    );

    const handleConfirmModalConfirm = useCallback(() => {
        if (state.confirmModal?.type === 'delete') {
            deleteForm(state.confirmModal.data.id)
                .then(() => {
                    page.current = 0;
                    getFormsList();
                })
                .catch((err) => {
                    console.warn(err);
                    showToast();
                })
                .finally(() => dispatch({ type: 'setConfirmModal', data: null }));
        }
    }, [getFormsList, showToast, state.confirmModal]);

    const handleEditForm = useCallback(
        (id, idFormType) => {
            if (!state.actionsReady) {
                showToast();
                return;
            }
            bridgeBackend
                .setContext({ form: { id, formTemplate: { id: idFormType } } })
                .then(() => getForm(id))
                .then(({ data }) => dispatch({ type: 'editForm', data }))
                .catch((err) => {
                    console.warn(err);
                    showToast();
                });
        },
        [bridgeBackend, showToast, state.actionsReady],
    );

    const handleDeleteForm = useCallback(
        (id) =>
            dispatch({
                type: 'setConfirmModal',
                data: {
                    type: 'delete',
                    size: 'small',
                    title: t('title_warning'),
                    text: t('confirm_delete'),
                    confirmText: t('cfm_action_yes'),
                    cancelText: t('cfm_action_no'),
                    overrides: { confirmButton: { type: 'primary-error' } },
                    data: { id },
                },
            }),
        [t],
    );

    const handleFormModalClose = useCallback(
        ({ forceReload }) => {
            bridgeBackend.setContext({ form: null });
            if (state.reload || forceReload) {
                page.current = 0;
                getFormsList();
            }
            dispatch({ type: 'closeForm' });
        },
        [bridgeBackend, getFormsList, state.reload],
    );

    const handleOpenDetail = useCallback(
        (id) => {
            if (!state.detailsDrawerIsOpen) {
                dispatch({ type: 'openDetail', data: state.forms.find((form) => form.id === id) });
            }
        },
        [state.detailsDrawerIsOpen, state.forms],
    );

    const handleCloseDetail = useCallback(() => dispatch({ type: 'closeDetail' }), []);

    const handleEditDetail = useCallback(() => {
        dispatch({ type: 'closeDetail' });
        handleEditForm(state.activeForm?.id);
    }, [handleEditForm, state.activeForm]);

    const handleConfirmSignature = useCallback(
        (signature) => {
            bridgeBackend.dispatchEvent('openSignatureView', signature);
            dispatch({ type: 'setSignatureIsOpen', value: false });
        },
        [bridgeBackend],
    );

    const handleCancelSignature = useCallback(
        () => dispatch({ type: 'setSignatureIsOpen', value: false }),
        [],
    );

    useEffect(() => {
        if (
            page.current !== 0 ||
            state.forms.length !== 0 ||
            !state.hasNextPage ||
            state.listIsLoading
        )
            return;
        dispatch({ type: 'setListIsLoading', value: true });
        setSize && setSize({ height: 120 });
        getFormTemplates()
            .then((res) => {
                const formTemplates = {};
                res.data.forEach((el) => (formTemplates[el.id] = el));
                dispatch({ type: 'setFormTemplates', data: formTemplates });
                getFormsList();
            })
            .catch((err) => {
                console.warn(err);
                showToast();
            });
    }, [
        entityId,
        getFormsList,
        showToast,
        setSize,
        state.forms.length,
        state.hasNextPage,
        state.listIsLoading,
    ]);

    const finishActivity = useCallback(
        () => handleFormModalClose({ forceReload: true }),
        [handleFormModalClose],
    );

    const saveData = useCallback(
        (id) => {
            dispatch({ type: 'setReload', value: true });

            const syncFormId = (formId) => {
                if (!formId) return;

                dispatch({ type: 'setActiveFormId', value: formId });
                bridgeBackend
                    .getContext()
                    .then(({ form }) =>
                        bridgeBackend.setContext({
                            form: {
                                ...form,
                                id: formId,
                            },
                        }),
                    )
                    .catch((err) => console.warn(err));
            };

            if (id) {
                syncFormId(id);
                return;
            }

            bridgeBackend
                .getContext()
                .then(({ form }) => syncFormId(form?.id ?? form?.Id))
                .catch((err) => console.warn(err));
        },
        [bridgeBackend],
    );

    const setTitle = useCallback((value) => dispatch({ type: 'setFormTitle', value }), []);

    const openSignatureView = useCallback(
        () => dispatch({ type: 'setSignatureIsOpen', value: true }),
        [],
    );

    const showCameraImages = useCallback(
        () => dispatch({ type: 'setShowImages', value: true }),
        [],
    );

    const hideCameraImages = useCallback(
        () => dispatch({ type: 'setShowImages', value: false }),
        [],
    );

    const setExpandImages = useCallback(
        (value) => dispatch({ type: 'setExpandImages', value }),
        [],
    );

    const expandImagesView = useCallback(
        () => dispatch({ type: 'setExpandImages', value: true }),
        [],
    );

    const collapseImagesView = useCallback(
        () => dispatch({ type: 'setExpandImages', value: false }),
        [],
    );

    const showLoading = useCallback(() => dispatch({ type: 'setIsLoading', value: true }), []);

    const hideLoading = useCallback(() => dispatch({ type: 'setIsLoading', value: false }), []);

    const showAlertDialog = useCallback((data) => dispatch({ type: 'setConfirmModal', data }), []);

    const showConfirmDialog = useCallback(
        (data) => dispatch({ type: 'setConfirmModal', data }),
        [],
    );

    useEffect(() => {
        const actions = {
            finishActivity,
            setTitle,
            saveData,
            openSignatureView,
            showCameraImages,
            hideCameraImages,
            expandImagesView,
            collapseImagesView,
            showLoading,
            hideLoading,
            showAlertDialog,
            showConfirmDialog,
        };
        if (state.actionsReady) return;
        bridgeBackend
            .setActions(actions)
            .then(() => dispatch({ type: 'setActionsReady' }))
            .catch((err) => {
                console.warn(err);
                dispatch({ type: 'setError' });
            });
    }, [
        bridgeBackend,
        collapseImagesView,
        expandImagesView,
        finishActivity,
        handleFormModalClose,
        hideCameraImages,
        hideLoading,
        openSignatureView,
        saveData,
        setFormId,
        setTitle,
        showAlertDialog,
        showCameraImages,
        showConfirmDialog,
        showLoading,
        state.actionsReady,
    ]);

    let stateComponent = (
        <FormsList
            items={state.forms}
            hasNextPage={state.hasNextPage}
            isLoading={state.listIsLoading}
            getFormsList={getFormsList}
            openDetail={handleOpenDetail}
            editForm={permissions.update ? handleEditForm : null}
            deleteForm={permissions.delete ? handleDeleteForm : null}
            height={height - 40}
        />
    );

    if (state.hasError) {
        stateComponent = (
            <div className={classes.error}>
                <Icon name="warning" className={classes.errorIcon} color={theme.colors.grey[100]} />
                <Text className={classes.errorText}>{t(`error_an_error_occurred`)}</Text>
            </div>
        );
    }

    if (!state.hasError && state.forms.length === 0 && !state.hasNextPage) {
        stateComponent = (
            <div className={classes.noForms}>
                <FormsIcon className={classes.noFormsIcon} color={theme.colors.grey[500]} />
                <Text type="caption" className={classes.noFormsText}>
                    {t(`helptext_empty_entity_forms`)}
                </Text>
            </div>
        );
    }

    if (
        (!state.hasError && state.forms.length === 0 && state.hasNextPage) ||
        (state.listIsLoading && state.reload)
    ) {
        stateComponent = (
            <div className={classes.loaderContainer}>
                <Loader className={classes.loader} />
            </div>
        );
    }

    return (
        <Fragment>
            {stateComponent}
            <Drawer
                side="right"
                isOpen={state.detailsDrawerIsOpen}
                onRequestClose={handleCloseDetail}
            >
                <FormDetails
                    form={state.activeForm}
                    onClose={handleCloseDetail}
                    onEdit={permissions.update ? handleEditDetail : undefined}
                />
            </Drawer>
            <Modal
                title={state.formTitle}
                className={classes.formModal}
                overlayClassName={classes.formModalOverlay}
                isOpen={state.formModalIsOpen}
                onRequestClose={handleFormModalClose}
            >
                <FormModal
                    formTemplates={state.formTemplates}
                    form={state.activeForm}
                    showImages={state.showImages}
                    expandImages={state.expandImages}
                    setExpandImages={setExpandImages}
                    isLoading={state.isLoading}
                />
            </Modal>
            <Modal
                title={state.confirmModal?.title || null}
                isOpen={!!state.confirmModal}
                size={state.confirmModal?.size || 'medium'}
                useCornerClose={false}
                onRequestClose={handleConfirmModalCancel}
                onCancel={handleConfirmModalCancel}
                onConfirm={handleConfirmModalConfirm}
                cancelText={state.confirmModal?.cancelText || t('action_cancel')}
                confirmText={state.confirmModal?.confirmText || t('action_accept')}
                overrides={state.confirmModal?.overrides}
                overlayClassName={classes.confirmModalOverlay}
            >
                <Text>{state.confirmModal?.text || null}</Text>
            </Modal>
            <Signature
                isOpen={state.signatureIsOpen}
                onConfirm={handleConfirmSignature}
                onCancel={handleCancelSignature}
                overlayClassName={classes.signatureOverlay}
            />
        </Fragment>
    );
};

Forms.propTypes = {
    entityId: PropTypes.string.isRequired,
    add: PropTypes.bool.isRequired,
    setAdd: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
    setSize: PropTypes.func,
};

export default memo(Forms);
